import React, { useMemo } from "react";
import ReusableFilterDrawer from "../../component/common/ReusableFilterDrawer";
import CustomMultiSelect from "../../component/Layout/DataContract/Viewkpi/CustomMultiSelect";

const toOptions = (arr = []) =>
  (arr || [])
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
    .map((v) => {
      const str = String(v);
      return { label: str.charAt(0).toUpperCase() + str.slice(1), value: str };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

const AccessFilterDrawer = ({
  open = true,
  onClose,
  filters,
  appliedFilters,
  onApply,
  onReset,
  showAccessCategory = true,
}) => {
  const parentDomainsData = useMemo(
    () => filters?.parent_domains || filters?.parentDomains || [],
    [filters],
  );

  const deriveFromParents = (selectedParents = []) => {
    const domainsSet = new Set();
    const subDomainsSet = new Set();
    const productsSet = new Set();
    parentDomainsData.forEach((pd) => {
      if (selectedParents.length === 0 || selectedParents.includes(pd.parent_domain_id)) {
        (pd.domains || []).forEach((d) => {
          domainsSet.add(d.domain_id);
          (d.sub_domains || []).forEach((sd) => {
            subDomainsSet.add(sd.sub_domain_id);
            (sd.products || []).forEach((p) => productsSet.add(p));
          });
        });
      }
    });
    return {
      domains: Array.from(domainsSet),
      subDomains: Array.from(subDomainsSet),
      products: Array.from(productsSet),
    };
  };

  const findHierarchyByProduct = (product) => {
    for (const pd of parentDomainsData) {
      for (const d of pd.domains || []) {
        for (const sd of d.sub_domains || []) {
          if ((sd.products || []).includes(product)) {
            return { parent: pd.parent_domain_id, domain: d.domain_id, subDomain: sd.sub_domain_id };
          }
        }
      }
    }
    return null;
  };

  const sections = useMemo(() => {
    const fields = [
      {
        type: "custom",
        key: "parentDomain",
        render: (localFilters, setLocalFilters) => {
          const opts = parentDomainsData
            .map((pd) => ({ label: pd.parent_domain, value: pd.parent_domain_id }))
            .sort((a, b) => a.label.localeCompare(b.label));
          return (
            <div className="mb-4">
              <CustomMultiSelect
                label="Parent Domain"
                options={opts}
                value={opts.filter((o) => (localFilters.parentDomain || []).includes(o.value))}
                onChange={(_, v) => {
                  const selected = (v || []).map((o) => o.value);
                  setLocalFilters((prev) => {
                    const derived = deriveFromParents(selected);
                    return {
                      ...prev,
                      parentDomain: selected,
                      domain: (prev.domain || []).filter((d) => derived.domains.includes(d)),
                      subDomain: (prev.subDomain || []).filter((s) => derived.subDomains.includes(s)),
                      product: (prev.product || []).filter((p) => derived.products.includes(p)),
                    };
                  });
                }}
                placeholder="Select Parent Domain"
              />
            </div>
          );
        },
      },
      {
        type: "custom",
        key: "domain",
        render: (localFilters, setLocalFilters) => {
          const result = [];
          parentDomainsData.forEach((pd) => {
            if (localFilters.parentDomain?.length > 0 && !localFilters.parentDomain.includes(pd.parent_domain_id)) return;
            (pd.domains || []).forEach((d) => {
              if (!result.some((r) => r.value === d.domain_id)) {
                result.push({ label: d.domain, value: d.domain_id });
              }
            });
          });
          const opts = result.sort((a, b) => a.label.localeCompare(b.label));
          return (
            <div className="mb-4">
              <CustomMultiSelect
                label="Domain"
                options={opts}
                value={opts.filter((o) => (localFilters.domain || []).includes(o.value))}
                onChange={(_, v) => {
                  const selected = (v || []).map((o) => o.value);
                  setLocalFilters((prev) => {
                    const next = { ...prev, domain: selected };
                    if (selected.length > 0 && (prev.parentDomain || []).length === 0) {
                      const parentSet = new Set();
                      parentDomainsData.forEach((pd) => {
                        (pd.domains || []).forEach((d) => {
                          if (selected.includes(d.domain_id)) parentSet.add(pd.parent_domain_id);
                        });
                      });
                      if (parentSet.size > 0) next.parentDomain = Array.from(parentSet);
                    }
                    return next;
                  });
                }}
                placeholder="Select Domain"
              />
            </div>
          );
        },
      },
      {
        type: "custom",
        key: "subDomain",
        render: (localFilters, setLocalFilters) => {
          const result = [];
          parentDomainsData.forEach((pd) => {
            if (localFilters.parentDomain?.length > 0 && !localFilters.parentDomain.includes(pd.parent_domain_id)) return;
            (pd.domains || []).forEach((d) => {
              if (localFilters.domain?.length > 0 && !localFilters.domain.includes(d.domain_id)) return;
              (d.sub_domains || []).forEach((sd) => {
                if (!result.some((r) => r.value === sd.sub_domain_id)) {
                  result.push({ label: sd.sub_domain, value: sd.sub_domain_id });
                }
              });
            });
          });
          const opts = result.sort((a, b) => a.label.localeCompare(b.label));
          return (
            <div className="mb-4">
              <CustomMultiSelect
                label="Sub Domain"
                options={opts}
                value={opts.filter((o) => (localFilters.subDomain || []).includes(o.value))}
                onChange={(_, v) => {
                  const selected = (v || []).map((o) => o.value);
                  setLocalFilters((prev) => {
                    const next = { ...prev, subDomain: selected };
                    if (selected.length > 0) {
                      const parentSet = new Set(prev.parentDomain || []);
                      const domainSet = new Set(prev.domain || []);
                      parentDomainsData.forEach((pd) => {
                        (pd.domains || []).forEach((d) => {
                          (d.sub_domains || []).forEach((sd) => {
                            if (selected.includes(sd.sub_domain_id)) {
                              if ((prev.parentDomain || []).length === 0) parentSet.add(pd.parent_domain_id);
                              if ((prev.domain || []).length === 0) domainSet.add(d.domain_id);
                            }
                          });
                        });
                      });
                      next.parentDomain = Array.from(parentSet);
                      next.domain = Array.from(domainSet);
                    }
                    return next;
                  });
                }}
                placeholder="Select Sub Domain"
              />
            </div>
          );
        },
      },
      {
        type: "custom",
        key: "product",
        render: (localFilters, setLocalFilters) => {
          const pSet = new Set();
          parentDomainsData.forEach((pd) => {
            if (localFilters.parentDomain?.length > 0 && !localFilters.parentDomain.includes(pd.parent_domain_id)) return;
            (pd.domains || []).forEach((d) => {
              if (localFilters.domain?.length > 0 && !localFilters.domain.includes(d.domain_id)) return;
              (d.sub_domains || []).forEach((sd) => {
                if (localFilters.subDomain?.length > 0 && !localFilters.subDomain.includes(sd.sub_domain_id)) return;
                (sd.products || []).forEach((p) => pSet.add(p));
              });
            });
          });
          const opts = Array.from(pSet)
            .map((v) => ({ label: v, value: v }))
            .sort((a, b) => a.label.localeCompare(b.label));
          return (
            <div className="mb-4">
              <CustomMultiSelect
                label="Product"
                options={opts}
                value={opts.filter((o) => (localFilters.product || []).includes(o.value))}
                onChange={(_, v) => {
                  const selected = (v || []).map((o) => o.value);
                  setLocalFilters((prev) => {
                    const next = { ...prev, product: selected };
                    if (selected.length > 0) {
                      const parentSet = new Set(prev.parentDomain || []);
                      const domainSet = new Set(prev.domain || []);
                      const subSet = new Set(prev.subDomain || []);
                      selected.forEach((p) => {
                        const h = findHierarchyByProduct(p);
                        if (h) {
                          if ((prev.parentDomain || []).length === 0) parentSet.add(h.parent);
                          if ((prev.domain || []).length === 0) domainSet.add(h.domain);
                          if ((prev.subDomain || []).length === 0) subSet.add(h.subDomain);
                        }
                      });
                      next.parentDomain = Array.from(parentSet);
                      next.domain = Array.from(domainSet);
                      next.subDomain = Array.from(subSet);
                    }
                    return next;
                  });
                }}
                placeholder="Select Product"
              />
            </div>
          );
        },
      },
    ];

    if (showAccessCategory) {
      fields.push({
        type: "custom",
        key: "category",
        render: (localFilters, setLocalFilters) => {
          const opts = toOptions(filters?.accessCategory || []);
          return (
            <div className="mb-4">
              <CustomMultiSelect
                label="Access Category"
                options={opts}
                value={opts.filter((o) => (localFilters.category || []).includes(o.value))}
                onChange={(_, v) => {
                  const selected = (v || []).map((o) => o.value);
                  setLocalFilters((prev) => ({ ...prev, category: selected }));
                }}
                placeholder="Select Access Category"
              />
            </div>
          );
        },
      });
    }

    fields.push(
      { type: "date", key: "startDate", label: "Start Date", defaultValue: null },
      { type: "date", key: "endDate", label: "End Date", defaultValue: null },
    );

    return [{ fields }];
  }, [parentDomainsData, showAccessCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const values = useMemo(() => ({
    parentDomain: appliedFilters?.parentDomain || [],
    domain: appliedFilters?.domain || [],
    subDomain: appliedFilters?.subDomain || [],
    product: appliedFilters?.product || [],
    category: appliedFilters?.category || [],
    startDate: appliedFilters?.startDate || null,
    endDate: appliedFilters?.endDate || null,
  }), [appliedFilters]);

  return (
    <ReusableFilterDrawer
      open={open}
      onClose={onClose}
      sections={sections}
      values={values}
      onApply={onApply}
      onReset={onReset}
      flat
      subtitle="Filter by domain, product & date"
    />
  );
};

export default AccessFilterDrawer;
